import { AfterContentInit, Component, DestroyRef, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ActivitySectionDirective,
  HappyHoursService,
  IMenuWithPages,
  IMenuWithPagesAndIcon,
  MenuImageComponent,
  MenusService,
  UserActivityClickTrackingDirective,
} from '@core';
import { LoadingComponent } from '@app/components/loading/loading.component';
import { shareReplay } from 'rxjs';

const mainTabId = 'tabMain';
const happyHoursTabId = 'tabHappyHours';

// @TODO: User signals.

@Component({
  selector: 'app-menus-tab',
  templateUrl: 'menu-tab.component.html',
  styleUrl: 'menu-tab.component.scss',
  imports: [
    LoadingComponent,
    UserActivityClickTrackingDirective,
    ActivitySectionDirective,
    LoadingComponent,
    LoadingComponent,
    MenuImageComponent,
    TranslatePipe,
  ],
  host: {
    'class': 'page',
  },
})
export class MenuTabComponent implements OnInit, AfterContentInit {

  private readonly destroyRef = inject(DestroyRef);
  private readonly element = inject(ElementRef);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly menusService = inject(MenusService);
  private readonly happyHoursService = inject(HappyHoursService);

  public readonly tabs = {
    isSticky: false,
    activeId: mainTabId,
    name: '',
  };

  public readonly pinchZoomProperties = {
    wheel: false,
    autoZoomOut: true,
    backgroundColor: 'rgba(240, 240, 243, 0.9)',
  };

  public isLoading = toSignal(this.menusService.loading$, {
    initialValue: true,
  });

  public isSyncInProgress = toSignal(this.menusService.sync$, {
    requireSync: true,
  });

  public readonly currentLanguage = toSignal(this.translate.onLangChange.pipe(
    map((event) => {
      return event.lang;
    }),
    shareReplay(1),
  ), { initialValue: this.translate.getCurrentLang() });

  public mainMenus: IMenuWithPagesAndIcon[] = [];
  public mainMenusCurrent: IMenuWithPages | null = null;

  public happyHoursMenus: IMenuWithPagesAndIcon[] = [];
  public happyHoursMenusCurrent: IMenuWithPages | null = null;

  get isBackTabShow(): boolean {
    return (
        this.tabs.activeId === 'tabMain'
        && this.mainMenus.length > 0
        && !!this.mainMenusCurrent
      )
      || (
        this.tabs.activeId === 'tabHappyHours'
        && this.happyHoursMenus.length > 0
        && !!this.happyHoursMenusCurrent
      );
  }

  public get isTabsShow(): boolean {
    return this.isBackTabShow || (
      this.happyHoursMenus.length !== 0
      && this.mainMenus.length !== 0
    );
  }

  ngOnInit(): void {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.setActiveTab(this.tabs.activeId);
    });
  }

  ngAfterContentInit(): void {
    this.menusService.menusWithPagesAndIcons$.pipe(
      tap((menus) => {
        if (menus) {
          this.updateMenus(menus);
        }
      }),
      switchMap(() => this.happyHoursService.isNow$.pipe(
        take(1),
      )),
      tap((isHH) => {
        isHH =
          (
            isHH || this.mainMenus.length === 0
          )
          && this.happyHoursMenus.length !== 0;
        this.setActiveTab(isHH ? happyHoursTabId : mainTabId);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  @HostListener('scroll')
  onScroll(): void {
    this.tabs.isSticky = this.element.nativeElement.scrollTop >= 20;
  }

  private updateMenus(menus: IMenuWithPagesAndIcon[]): void {
    this.mainMenus = menus.filter(
      (menu) => {
        return (
          (
            !menu.happyHours && !menu.hidden
          ) &&
          (
            this.currentLanguage() === undefined || menu.language === this.currentLanguage() || menu.language === null
          )
        );
      },
    );
    this.happyHoursMenus = menus.filter(
      (menu) => {
        return (
          (
            menu.happyHours && !menu.hidden
          ) &&
          (
            this.currentLanguage() === undefined || menu.language === this.currentLanguage() || menu.language === null
          )
        );
      },
    );

    this.clearMenusCurrents();

    if (this.mainMenus.length === 0 && this.happyHoursMenus.length !== 0) {
      this.setActiveTab(happyHoursTabId);
    }

    if (this.mainMenus.length !== 0 && this.happyHoursMenus.length === 0) {
      this.setActiveTab(mainTabId);
    }
  }

  setMainMenusCurrent(menu: IMenuWithPages | null): void {
    this.mainMenusCurrent = menu;
  }

  setHappyHoursMenusCurrent(menu: IMenuWithPages | null): void {
    this.happyHoursMenusCurrent = menu;
  }

  clearMenusCurrents(): void {
    this.setMainMenusCurrent(null);
    this.setHappyHoursMenusCurrent(null);
  }

  setActiveTab(id: string, name?: string): void {
    this.tabs.activeId = id;
    if (name) {
      this.tabs.name = name;
    }

    this.clearMenusCurrents();

    this.element.nativeElement.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

}
